<template>
  <v-dialog v-model="dialog" persistent :width="$vuetify.display.smAndDown ? 400 : 677">
    <AlFormCard title="Vehicle Log Detail" :subtitle="subtitle">
      <template #error>
        <ErrorAlert
          v-if="error != null"
          :error="error"
          @clearErrors="clearErrors()"
          class="mt-0 mb-4 rounded"
        />
      </template>

      <template #appendAction>
        <v-btn
          variant="tonal"
          class="rounded"
          size="32"
          @click="closeDialog()"
          icon
          color="default"
          density="comfortable"
        >
          <v-icon size="18" icon="mdi-close" />
        </v-btn>
      </template>

      <v-form @submit.prevent ref="form" v-model="valid" validate-on="submit lazy">
        <v-row>
          <v-col cols="6">
            <v-text-field
              v-model="vehicleLogData.mileage"
              label="Mileage"
              type="number"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :error-messages="error && (error.fieldErrors['mileage'] as string[])"
            ></v-text-field>
          </v-col>
          <v-col cols="6">
            <v-text-field
              v-model="vehicleLogData.fuel_percentage"
              label="Fuel Percentage"
              max="100"
              min="0"
              step="1"
              type="number"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :error-messages="error && (error.fieldErrors['fuel_percentage'] as string[])"
              suffix="%"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="vehicleLogData.notes"
              label="Notes"
              variant="outlined"
              hide-details="auto"
              density="compact"
              @update:model-value="clearErrors()"
              :error-messages="error && (error.fieldErrors['notes'] as string[])"
            ></v-textarea>
          </v-col>
        </v-row>
      </v-form>

      <template #actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" :loading="loading" @click="save()">Save</v-btn>
      </template>
    </AlFormCard>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { cloneDeep } from 'lodash'
import { diffDeep } from '@/utils/helpers'

import { useCreateVehicleLog, useUpdateVehicleLog } from '@/composables/vehicle-log'

import {
  VehicleLog,
  type IVehicleLog,
  type IVehicleLogData
} from '@/models/fleet-management/vehicle'
import type { ISystemError } from '@/models/error'
import type { VForm } from 'vuetify/components'

import AlFormCard from '@/components/common/AlFormCard.vue'
import ErrorAlert from '@/components/common/ErrorAlert.vue'

interface Props {
  vehicleLog?: IVehicleLog
  isEdit: boolean
}

const props = defineProps<Props>()

const dialog = defineModel<boolean>('dialog', { default: false })

const vehicleLogData = ref<IVehicleLogData>({})

watch(dialog, (value) => {
  if (value) {
    clearErrors()
    vehicleLogData.value = !props.isEdit ? ({} as IVehicleLogData) : cloneDeep(props.vehicleLog!)
  }
})

const form = ref<VForm>()
const valid = ref(false)

const subtitle = computed(() => `${props.isEdit ? 'Update' : 'Create'} vehicle log information`)

function closeDialog() {
  clearErrors()
  dialog.value = false
}

const {
  isPending: isUpdatePending,
  error: updateError,
  mutate: updateVehicleLog,
  reset: updateReset
} = useUpdateVehicleLog()
const {
  isPending: isCreatePending,
  error: createError,
  mutate: createVehicleLog,
  reset: createReset
} = useCreateVehicleLog()

const localError = ref<ISystemError | null>(null)

const loading = computed(() => (props.isEdit ? isUpdatePending.value : isCreatePending.value))

const error = computed({
  get: () => {
    return localError.value || props.isEdit ? updateError.value : createError.value
  },
  set: (value) => {
    localError.value = value
  }
})

function clearErrors() {
  props.isEdit ? updateReset() : createReset()
  error.value = null
}

async function save() {
  clearErrors()
  const { valid } = await form.value!.validate()

  if (valid) {
    let vehicleLogInstance: IVehicleLog | null = null

    try {
      vehicleLogInstance = new VehicleLog(vehicleLogData.value)
    } catch (e: any) {
      // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
      error.value = e
    }

    if (vehicleLogInstance != null) {
      const saveAction = props.isEdit ? updateVehicleLog : createVehicleLog

      let payload = vehicleLogInstance
      if (props.isEdit) {
        payload = diffDeep(vehicleLogInstance, props.vehicleLog, true)
      }

      saveAction(payload, {
        onSuccess: () => {
          closeDialog()
        }
      })
    }
  }
}
</script>
